import React, {useMemo} from 'react'
import {
  DateTime,
  ErrorLoadingRetry,
  useLocationCheckpointTourRun,
  UserName,
} from '@therms/react-modules'
import {Icon, Label, Loading, Tag, Timeline} from '@therms/atalaya'
import {keyBy} from 'lodash-es'
import dayjs from 'dayjs'
import {humanizeMinutes} from '@therms/web-js'
import {MdDoneOutline, MdGpsFixed, MdNfc, MdOutlineQrCode} from 'react-icons/md'

interface CheckpointScanDetailsProps {
  checkpointTourRunId: string
}

function CheckpointTourRunDetails({
  checkpointTourRunId,
}: CheckpointScanDetailsProps) {
  const {data, loading, refresh} = useLocationCheckpointTourRun({
    checkpointTourRunId,
  })

  const [checkpointsById, checkpointScans, tourRun] = useMemo(() => {
    if (!data) return []

    return [
      keyBy(data.checkpoints, 'id'),
      data.checkpointScans,
      data.checkpointTourRun,
    ]
  }, [data])

  if (loading && !data) return <Loading overlay />

  if (!checkpointsById || !checkpointScans || !tourRun)
    return <ErrorLoadingRetry onRetry={refresh} />

  return (
    <div>
      <div className="text-lg">
        {tourRun.checkpointTour.name}{' '}
        {tourRun.status === 'inprogress' ? (
          <Tag value="In-Progress" variant="main" />
        ) : (
          <Tag value="Ended" variant="neutral" />
        )}
      </div>

      <div className="mt-base">
        <div className="grid grid-cols-2">
          <div className="flex flex-col">
            <Label>Started</Label>
            <DateTime timestamp={tourRun.startDate} />
          </div>

          {tourRun.status === 'ended' ? (
            <div className="flex flex-col">
              <Label>Ended</Label>
              {tourRun.endDate && (
                <div>
                  <DateTime mode="time" timestamp={tourRun.endDate} />{' '}
                  <span className="text-color-neutral font-light">
                    (
                    {humanizeMinutes(
                      dayjs(tourRun.startDate).diff(tourRun.endDate, 'minutes'),
                    )}
                    )
                  </span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col justify-end">
              <em className="text-color-neutral">in-progress</em>
            </div>
          )}
        </div>
      </div>

      <div className="mb-base" />

      <progress
        className="w-full"
        max={tourRun.checkpointScans.length}
        value={tourRun.checkpointScansCompleted}
      />

      {tourRun.checkpointScansRemaining > 0 && (
        <div className="my-sm">
          <div className="my-sm text-color-neutral">
            {tourRun.checkpointScansRemaining} scans incomplete
          </div>

          <div className="ml-base border-border pb-base border-b">
            {tourRun.checkpointScans
              .filter((scan) => !scan.scanned)
              .map((scan) => {
                const checkpoint = checkpointsById[scan.checkpointId]

                return (
                  <div>
                    <span className="uppercase">
                      <Icon
                        className="mr-xs"
                        Icon={
                          checkpoint.type === 'qr' ? MdOutlineQrCode : MdNfc
                        }
                      />
                      <span className="text-color-neutral-faded font-light">
                        {checkpoint.type}{' '}
                      </span>
                    </span>
                    {checkpoint.name}
                    <div className="text-color-neutral text-sm">
                      {checkpoint.description}
                    </div>
                  </div>
                )
              })}
          </div>
        </div>
      )}

      <div className="ml-sm mt-base">
        <Timeline
          icon={<Icon Icon={MdDoneOutline} />}
          iconBgColor="positive"
          data={checkpointScans.map(({scan}) => scan)}
          render={(scan: (typeof checkpointScans)[number]['scan']) => {
            const checkpoint = checkpointsById[scan.checkpointId]

            return (
              <div>
                <span className="uppercase">
                  <Icon
                    className="mr-xs"
                    Icon={checkpoint.type === 'qr' ? MdOutlineQrCode : MdNfc}
                  />
                  <span className="text-color-neutral-faded font-light">
                    {checkpoint.type}{' '}
                  </span>
                </span>
                {checkpoint.name}
              </div>
            )
          }}
          renderTimestamp={(scan) => (
            <div>
              <DateTime mode="time" timestamp={scan.timestamp} />
              <span className="ml-sm">
                <UserName userId={scan.userId} />
                {scan.geo && (
                  <span className="ml-sm opacity-50">
                    {scan.geo.coords && (
                      <Icon color="main" Icon={MdGpsFixed} size="sm" />
                    )}{' '}
                    {scan.geo.device.os} {scan.geo.device.name}
                  </span>
                )}
              </span>
            </div>
          )}
        />
      </div>
    </div>
  )
}

export {CheckpointTourRunDetails}
